import React, { useEffect } from "react";
import PropTypes from "prop-types";
import {
  Combobox,
  Option,
  Avatar,
  useId,
  Tooltip,
  Checkbox,
  makeStyles,
} from "@fluentui/react-components";
import { FluentField } from "./Fluent-Field";

const useStyles = makeStyles({
  root: {
    fontWeight: 600,
    textAlign: "left",
    width: "100%",
    color: "#4E4E4E",
  },
  chipsWrap: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "6px",
    paddingBottom: "16px",
    borderBottom: "1px solid #E0E0E0",
    marginTop: "16px",
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: "9999px",
    background: "#e1dfddc5",
    border: "1px solid var(--colorNeutralStroke2, #e1dfdd)",
    lineHeight: 1,
    userSelect: "none",
  },
  pillText: {
    maxWidth: 200,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    paddingLeft: "4px",
    paddingRight: "2px"
  },
  pillClose: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    cursor: "pointer",
    fontWeight: 700,
    lineHeight: 1,
    opacity: 0.75,
    ":hover": { opacity: 1, background: "rgba(0,0,0,0.06)" },
  },
  optionRow: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
  },
  selectAllCheckbox: {
    marginLeft: "-10px",
    marginRight: "-4px",
  },
});

export const FluentComboboxOptionAsObject = ({
  label,
  placeholder,
  required,
  disabled,
  options,
  selectedOptions,
  onSelectionChange,
  multiselect = false,
  showAvatars = false,
  avatarColor = "colorful",
  showPills = false,
}) => {
  const comboId = useId("combobox");
  const comboboxRef = React.useRef(null);
  const classes = useStyles();

  const [isFocused, setIsFocused] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");

  const blurCombo = () => setTimeout(() => comboboxRef.current?.blur(), 0);
  const normalizedSelected = React.useMemo(
    () => (selectedOptions || []).map((x) => String(x)),
    [selectedOptions]
  );

  const idToName = React.useMemo(() => {
    const m = new Map();
    (options || []).forEach((o) => m.set(String(o.id), o.name));
    return m;
  }, [options]);

  const singleDisplay = React.useMemo(() => {
    if (multiselect) return "";
    const first = normalizedSelected[0];
    return first ? idToName.get(first) || "" : "";
  }, [multiselect, normalizedSelected, idToName]);

  useEffect(() => {
    if (!isFocused) setInputValue(singleDisplay);
  }, [isFocused, singleDisplay]);

  const handleFocus = () => {
    setIsFocused(true);
    setInputValue("");
  };

  const handleBlur = () => {
    setIsFocused(false);
    setInputValue(singleDisplay);
  };

  const handleInputChange = (e) => setInputValue(e.target.value);

  const SELECT_ALL_KEY = "__select_all__";

  const allIds = React.useMemo(() => (options || []).map(o => String(o.id)), [options]);
  const allSelected = normalizedSelected.length > 0 && normalizedSelected.length === allIds.length;
  const noneSelected = normalizedSelected.length === 0;
  const mixedSelected = !noneSelected && !allSelected;

  const handleOptionSelect = (_e, data) => {
    const clicked = data.optionValue;

    if (!multiselect && normalizedSelected[0] === String(clicked)) {
      onSelectionChange?.([]);
      setInputValue("");
      blurCombo();
      return;
    }
    if (multiselect && clicked === SELECT_ALL_KEY) {
      const next = allSelected ? [] : allIds;
      onSelectionChange?.(next);
      if (multiselect) setInputValue("");
      return;
    }

    const next = (data.selectedOptions || []).map(String);
    onSelectionChange?.(next);
    if (multiselect) {
      setInputValue("");
    } else {
      blurCombo(); 
    }
  };

  const dismissId = (id) =>
    onSelectionChange?.(normalizedSelected.filter((x) => x !== id));

const pills =
  showPills && multiselect && normalizedSelected.length > 0 ? (
    <div className={classes.chipsWrap}>
      {normalizedSelected.map((id) => {
        const name = idToName.get(id) || id;
        const isLong = name.length > 30;
        const display = isLong ? `${name.slice(0, 30)}…` : name;

        const TextContent = (
          <span className={classes.pillText}>{display}</span>
        );

        return (
          <span
            key={id}
            className={classes.pill}
            onMouseDown={(e) => e.preventDefault()}
          >
            {showAvatars && (
              <Avatar
                name={name}
                color={avatarColor}
                shape="circular"
                size={24}
              />
            )}

            {isLong ? (
              <Tooltip content={name} relationship="label">
                {TextContent}
              </Tooltip>
            ) : (
              TextContent
            )}

            <Tooltip content="Remove" relationship="label">
              <span
                className={classes.pillClose}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  dismissId(id);
                }}
                role="button"
                aria-label={`Remove ${name}`}
              >
                ×
              </span>
            </Tooltip>
          </span>
        );
      })}
    </div>
  ) : null;

  return (
    <FluentField label={label} required={required} componentStyle={classes.root}>
      <Combobox
        ref={comboboxRef}
        aria-labelledby={comboId}
        placeholder={placeholder}
        disabled={disabled}
        filter="contains"
        multiselect={multiselect}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        selectedOptions={normalizedSelected}
        onOptionSelect={handleOptionSelect}
        style={{ width: "100%", minWidth: 0 }}
        listbox={{ style: { width: "100%", minWidth: 0 } }}
        positioning={{ matchTriggerWidth: true }}
      >
        {multiselect && (
          <Option
            value={SELECT_ALL_KEY}
            text="__select_all__"
            checkIcon={null}
          >
            <div
              className={classes.optionRow}
              onMouseDown={(e) => e.preventDefault()}
            >
              <Checkbox
                checked={allSelected ? true : mixedSelected ? "mixed" : false}
                onChange={(e) => e.preventDefault()}
                tabIndex={-1}
                className={classes.selectAllCheckbox}
              />
              <span> Select all </span>
            </div>
          </Option>
        )}
        {(options || []).map((o) => (
          <Option key={String(o.id)} value={String(o.id)} text={o.name}>
            <div className={classes.optionRow}>
              {showAvatars && (
                <Avatar name={o.name} color={avatarColor} shape="circular"/>
              )}
              <span>{o.name}</span>
            </div>
          </Option>
        ))}
      </Combobox>
      {pills}
    </FluentField>
  );
};

FluentComboboxOptionAsObject.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedOptions: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  onSelectionChange: PropTypes.func.isRequired,
  multiselect: PropTypes.bool,
  showAvatars: PropTypes.bool,
  avatarColor: PropTypes.string,
  showPills: PropTypes.bool,
};